import {
  boardService,
  type AnnouncementSearchRequest,
  type AnnouncementUpsertRequest,
  type FaqUpsertRequest,
} from '@/entities/board/api/board.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const boardKeys = {
  all: ['board'] as const,
  faqs: () => [...boardKeys.all, 'faqs'] as const,
  announcements: (
    search: AnnouncementSearchRequest,
    pageable: Pageable,
  ) => [...boardKeys.all, 'announcements', search, pageable] as const,
  announcement: (id: number) =>
    [...boardKeys.all, 'announcement', id] as const,
};

export const useFaqs = () =>
  useQuery({
    queryKey: boardKeys.faqs(),
    queryFn: () => boardService.getFaqs(),
  });

export const useCreateFaq = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FaqUpsertRequest) => boardService.createFaq(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boardKeys.faqs() });
    },
  });
};

export const useUpdateFaq = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      faqId,
      data,
    }: {
      faqId: number;
      data: FaqUpsertRequest;
    }) => boardService.updateFaq(faqId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boardKeys.faqs() });
    },
  });
};

export const useDeleteFaq = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (faqId: number) => boardService.deleteFaq(faqId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boardKeys.faqs() });
    },
  });
};

export const useAnnouncements = (
  search: AnnouncementSearchRequest,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: [...boardKeys.all, 'announcements'],
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      boardService.getAnnouncements(s, p),
  });

export const useAnnouncement = (id: number) =>
  useQuery({
    queryKey: boardKeys.announcement(id),
    queryFn: () => boardService.getAnnouncement(id),
    enabled: id > 0,
  });

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: AnnouncementUpsertRequest) =>
      boardService.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: boardKeys.all });
    },
  });
};

export const useUpdateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: AnnouncementUpsertRequest;
    }) => boardService.updateAnnouncement(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: boardKeys.announcement(id) });
      queryClient.invalidateQueries({ queryKey: boardKeys.all });
    },
  });
};

export const useUploadAnnouncementImage = () =>
  useMutation({
    mutationFn: (file: File) => boardService.uploadAnnouncementImage(file),
  });
